import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, FileSignature, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { QUOTE_CATEGORIES, QUOTE_STATUS, QUOTE_TONE, fmtDateTime, fmtDay, fmtEuro } from "@/lib/format";
import { Btn, Chip, Empty, Modal, PageHeader, Pill, Spinner } from "@/components/rp";
import { AccessData, DocItem, Section } from "@/components/commesse";

const SurveyCard = ({ q, act }) => {
  const s = q.survey;
  if (!s || !["proposto", "programmato"].includes(s.status)) return null;
  return (
    <Section label="Sopralluogo" testid="client-quote-survey">
      {s.status === "proposto" ? (
        <>
          <p className="text-sm mb-3">Scegli l'orario che preferisci per il sopralluogo ({s.location}):</p>
          <div className="flex flex-wrap gap-2">{s.proposals.map((p, i) => <Chip key={i} onClick={() => act("survey/choose", { index: i })} data-testid={`client-survey-slot-${i}`}>{fmtDay(p.date)} · {p.start_time}{p.end_time && `–${p.end_time}`}</Chip>)}</div>
        </>
      ) : (
        <>
          <p className="font-medium">{fmtDay(s.date)} ore {s.start_time}{s.end_time && `–${s.end_time}`}</p>
          <p className="text-sm text-[#757575]">{s.location}{s.instructions && ` · ${s.instructions}`}</p>
          {s.client_confirmed_at ? <p className="text-sm text-[#198754] mt-2">Hai confermato l'appuntamento il {fmtDateTime(s.client_confirmed_at)}</p>
            : <Btn className="mt-3 h-10" onClick={() => act("survey/confirm")} data-testid="client-survey-confirm"><CalendarCheck className="h-4 w-4" /> Confermo l'appuntamento</Btn>}
        </>
      )}
    </Section>
  );
};

const AccessCard = ({ q, act, editable }) => {
  const [report, setReport] = useState("");
  const [open, setOpen] = useState(false);
  const pending = (q.logistics_reports || []).some((r) => !r.resolved_at);
  return (
    <Section label="Verifica i dati di accesso al cantiere" testid="client-quote-access">
      <p className="text-sm mb-3">{q.delivery_address}</p>
      <AccessData x={q} />
      {q.logistics_confirmed_at ? <p className="text-sm text-[#198754] mt-3">Hai confermato i dati il {fmtDateTime(q.logistics_confirmed_at)}</p>
        : pending ? <p className="text-sm text-[#8a6500] mt-3">In correzione da parte di RP Home: riceverai un messaggio quando potrai riconfermare.</p>
        : editable && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <Btn className="h-10" onClick={() => act("logistics", { confirm: true })} data-testid="client-access-confirm">I dati sono corretti</Btn>
              <Btn variant="outline" className="h-10" onClick={() => setOpen(!open)} data-testid="client-access-report-toggle">Segnala una correzione</Btn>
            </div>
            {open && <div className="flex gap-2"><input value={report} onChange={(e) => setReport(e.target.value)} className="rp-field" placeholder="Cosa va corretto?" data-testid="client-access-report-input" /><Btn variant="secondary" onClick={() => act("logistics", { report })} data-testid="client-access-report-send">Invia</Btn></div>}
          </div>
        )}
    </Section>
  );
};

const AcceptCard = ({ q, act }) => {
  const [ok, setOk] = useState(false);
  const [name, setName] = useState("");
  const [reject, setReject] = useState(false);
  const [note, setNote] = useState("");
  return (
    <Section label="Accettazione" className="bg-[#E6DDCE]/40 border-[#C8BDAB]" testid="client-quote-accept">
      {!q.logistics_confirmed_at && <p className="text-sm text-[#8a6500] mb-3">Prima di firmare conferma i dati di accesso al cantiere qui sopra.</p>}
      <label className="flex gap-3 text-sm items-start cursor-pointer">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-black" data-testid="client-accept-checkbox" />
        Dichiaro di aver letto i documenti del preventivo e di accettarlo. Verrà creato un PDF firmato e aperto il cantiere.
      </label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="rp-field mt-4 bg-white" placeholder="Nome e cognome (firma)" data-testid="client-accept-name" />
      <div className="flex flex-wrap gap-2 mt-4">
        <Btn disabled={!ok || name.trim().length < 3 || !q.logistics_confirmed_at} onClick={() => act("accept", { name, accept: true })} data-testid="client-accept-button"><FileSignature className="h-4 w-4" /> Accetto e firmo il preventivo</Btn>
        <Btn variant="danger" onClick={() => setReject(true)} data-testid="client-reject-button">Rifiuta</Btn>
      </div>
      <div className="mt-5 pt-4 border-t border-[#C8BDAB]/60">
        <p className="text-sm font-medium mb-1">Oppure scarica, firma e ricarica</p>
        <p className="text-xs text-[#757575] mb-3">Scarica i documenti qui sopra, firmali a mano o con la tua firma digitale e ricarica il PDF firmato: vale come accettazione.</p>
        <label className={`rp-btn bg-[#070707] text-white cursor-pointer h-10 ${!q.logistics_confirmed_at ? "opacity-50 pointer-events-none" : ""}`} data-testid="client-accept-upload"><Upload className="h-4 w-4" /> Carica il preventivo firmato
          <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f); act("accept-upload", fd); }} data-testid="client-accept-upload-input" /></label>
      </div>
      <Modal open={reject} onOpenChange={setReject} title="Rifiuta il preventivo" testid="client-reject-dialog">
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="rp-field" placeholder="Motivo (facoltativo)" data-testid="client-reject-note" />
        <div className="flex justify-end gap-2"><Btn variant="secondary" onClick={() => setReject(false)}>Annulla</Btn><Btn variant="danger" onClick={() => { setReject(false); act("reject", { note }); }} data-testid="client-reject-confirm">Conferma rifiuto</Btn></div>
      </Modal>
    </Section>
  );
};

const QuoteDetail = ({ q, setQ }) => {
  const act = async (path, body = {}) => {
    try { const { data } = await api.post(`/client/quotes/${q.id}/${path}`, body); setQ(data); toast.success("Fatto"); } catch (e) { toast.error(errMsg(e)); }
  };
  const open = ["inviato", "in_trattativa"].includes(q.status);
  return (
    <div className="space-y-5" data-testid="client-quote-detail">
      <div className="rp-card p-6 sm:p-8">
        <p className="text-xs font-mono text-[#757575]">{q.code} · {QUOTE_CATEGORIES[q.category] || q.category}</p>
        <h2 className="text-2xl font-semibold tracking-tight mt-1">{q.title}</h2>
        <p className="text-4xl sm:text-5xl font-semibold tracking-tight mt-4" data-testid="client-quote-amount">{fmtEuro(q.amount)}</p>
        <div className="flex flex-wrap items-center gap-3 mt-3"><Pill tone={QUOTE_TONE[q.status]}>{QUOTE_STATUS[q.status]}</Pill>{q.valid_until && <span className="text-xs text-[#757575]">Valido fino al {fmtDay(q.valid_until)}</span>}</div>
        {q.client_description && <p className="text-[15px] leading-relaxed mt-5 whitespace-pre-wrap">{q.client_description}</p>}
        {q.accepted_at && <p className="text-sm text-[#198754] mt-4 flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" />Preventivo accettato e firmato da {q.accepted_by} il {fmtDateTime(q.accepted_at)} · <Link to="/commessa" className="underline">Vai al cantiere</Link></p>}
      </div>
      <Section label="Documenti del preventivo">
        <div className="space-y-2">{q.documents.length ? q.documents.map((d) => <DocItem key={d.id} d={d} href={`/api/quotes/${q.id}/documents/${d.id}/file`} />) : <p className="text-sm text-[#757575]">Nessun documento allegato.</p>}</div>
      </Section>
      <SurveyCard q={q} act={act} />
      <AccessCard q={q} act={act} editable={open} />
      {open && <AcceptCard q={q} act={act} />}
    </div>
  );
};

export default function ClientPreventivi() {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/client/quotes").then((r) => { setList(r.data); if (r.data[0]) setSel(r.data[0].id); }); }, []);
  const [q, setQ] = useState(null);
  useEffect(() => { if (sel) api.get(`/client/quotes/${sel}`).then((r) => setQ(r.data)); }, [sel]);
  const update = (data) => { setQ(data); setList((l) => l.map((x) => (x.id === data.id ? data : x))); };

  if (!list) return <Spinner />;
  return (
    <div>
      <PageHeader label="Preventivi" title="I tuoi preventivi" />
      {!list.length ? <Empty icon={FileSignature} title="Nessun preventivo" text="Quando RP Home ti invierà un preventivo lo troverai qui, con documenti e firma online." data-testid="client-quotes-empty"><Link to="/richiesta-preventivo"><Btn className="mt-2">Richiedi un preventivo</Btn></Link></Empty> : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          <div className="space-y-2" data-testid="client-quotes-list">
            {list.map((x) => (
              <button key={x.id} onClick={() => setSel(x.id)} data-testid={`client-quote-${x.id}`} className={`w-full text-left rp-card p-4 transition-colors ${sel === x.id ? "border-black bg-white" : "hover:bg-white"}`}>
                <p className="text-xs font-mono text-[#757575]">{x.code}</p>
                <p className="font-medium">{x.title}</p>
                <div className="flex justify-between items-center mt-2"><span className="text-sm">{fmtEuro(x.amount)}</span><Pill tone={QUOTE_TONE[x.status]}>{QUOTE_STATUS[x.status]}</Pill></div>
              </button>
            ))}
          </div>
          {q && q.id === sel ? <QuoteDetail q={q} setQ={update} /> : <Spinner />}
        </div>
      )}
    </div>
  );
}
